<script>
  import { format } from 'svelte-i18n';
  import { Button, Modal, ModalBody, ModalFooter } from '@sveltestrap/sveltestrap';

  import { FileInput } from '@client/components/file-input';
  import { Icon } from '@client/components/icon';
  import { getNotificationsContext } from '@client/components/notifications';

  import { validateWorkflowForImport } from '@client/pages/staff/workflows/workflow-validation';

  import { importWorkflow } from '@client/stores/workflows';

  const { addNotification } = getNotificationsContext();

  export let show = false;

  let importing = false;
  let selectedFile = null;
  let workflowData = null;
  let error = '';

  const onFileLoad = (file) => {
    selectedFile = file;
    error = '';

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result);

        if (!jsonData.name || !jsonData.steps) {
          error = 'Invalid workflow file: Missing required fields (name, steps)';
          workflowData = null;
          return;
        }

        // Validate all step action values against known actions
        const validation = validateWorkflowForImport(jsonData);

        if (!validation.valid) {
          error = validation.errors[0]; // Show the first error to the user
          workflowData = null;
          return;
        }

        workflowData = jsonData;
        error = '';
      } catch (err) {
        error = 'Invalid JSON file';
        workflowData = null;
      }
    };

    reader.onerror = () => {
      error = 'Failed to read file';
      workflowData = null;
    };

    reader.readAsText(file);
  };

  const onFileRemove = () => {
    selectedFile = null;
    workflowData = null;
    error = '';
  };

  const onImport = async () => {
    if (!workflowData) {
      return;
    }

    importing = true;

    try {
      const newWorkflow = await importWorkflow(workflowData);

      addNotification({
        text: `Workflow "${newWorkflow.name}" imported successfully`,
        type: 'success',
        autohide: 3000,
        position: 'top-right'
      });

      resetModal();
    } catch (err) {
      error = err.message || 'Failed to import workflow';
      addNotification({
        text: error,
        type: 'error',
        autohide: 5000,
        position: 'top-right'
      });
    } finally {
      importing = false;
    }
  };

  const resetModal = () => {
    show = false;
    selectedFile = null;
    workflowData = null;
    error = '';
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      resetModal();
    }
  };

  $: show === false && resetModal();
  $: isDisabled = !workflowData || !!error;
</script>

<Modal bind:isOpen={show} size="md">
  <ModalBody class="import-workflow-modal">
    <button class="close" on:click={() => (show = !show)}>
      <Icon name="x" />
    </button>

    <div>
      <div class="title">
        <h1>Import Workflow</h1>
        <p>Upload a workflow JSON file to import it into your workflows.</p>
      </div>

      <div class="import-workflow-form">
        <div class="section">
          <h3>Workflow File</h3>
          <FileInput
            id="workflow-file-input"
            accept=".json,application/json"
            onLoad={onFileLoad}
            onRemove={onFileRemove}
          />
          {#if error}
            <div class="error-message text-danger mt-2">
              <small>{error}</small>
            </div>
          {/if}
          {#if workflowData && !error}
            <div class="workflow-preview mt-3">
              <div class="preview-item">
                <strong>Workflow Name:</strong>
                {workflowData.name}
              </div>
              <div class="preview-item">
                <strong>Steps:</strong>
                {workflowData.steps?.length || 0}
              </div>
            </div>
          {/if}
        </div>
      </div>
    </div>
  </ModalBody>
  <ModalFooter>
    <Button color="primary" disabled={isDisabled || importing} on:click={onImport}>Import Workflow</Button>
  </ModalFooter>
</Modal>

<svelte:window on:keydown={onKeyDown} />

<style lang="scss" src="./import.scss"></style>
